import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

const inputVariants = cva(
  "block w-full bg-bg-input text-text-primary rounded-[var(--radius-control)] border transition-colors " +
    "placeholder:text-text-tertiary " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus " +
    "disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      state: {
        default: "border-border",
        error: "border-error",
      },
      inputSize: {
        sm: "h-7 px-sm text-sm",
        md: "h-9 px-md text-base",
      },
    },
    defaultVariants: { state: "default", inputSize: "md" },
  }
);

type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> &
  VariantProps<typeof inputVariants> & {
    ref?: React.Ref<HTMLInputElement>;
  };

/**
 * Text input. Use `inputSize` instead of `size` (native HTML `size`
 * attribute is omitted to avoid a prop-name collision).
 *
 * @example
 * <Input placeholder="Email" />
 * <Input inputSize="sm" placeholder="Small" />
 * <Input state="error" defaultValue="bad" />
 */
export function Input({
  className,
  state,
  inputSize,
  ref,
  ...props
}: InputProps) {
  return (
    <input
      ref={ref}
      className={cn(inputVariants({ state, inputSize }), className)}
      {...props}
    />
  );
}
